import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Stack } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import CustomButton from 'components/button/CustomButton';
import ConditionList from 'components/condition-list/ConditionList';
import { ListPanelItem } from 'components/condition-list/ListPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import ConfirmDialog from 'components/dialog/ConfirmDialog';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import {
  AdminNoticeResult,
  ListAdminNoticeParams,
  NOTICE_TYPE_KEY,
  NoticeType,
  deleteAdminNotice,
  listAdminNotice,
  resolveAdminNotice,
} from 'stores/NoticeAdminStoreAdapter';
import { generateUUID } from 'utils/Common';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

import NoticeCreateForm, { NoticeCreateFormHandle } from './components/NoticeCreateForm';
import NoticeListItem from './components/NoticeListItem';
import NoticeUpdateForm, { NoticeUpdateFormHandle } from './components/NoticeUpdateForm';

type NoticeManagementPageProps = {
  noticeType: NoticeType;
  conditions: Condition[];
};

/**
 * 未解決フィルタの condition キー。API（ListAdminNoticeRequest）に未解決パラメータが無いため、
 * このキーだけは API に渡さずフロント側で isResolved === false を絞り込む特別扱いにする。
 */
const UNRESOLVED_FILTER_KEY = 'unresolved';

/** boolean として扱う検索キー（クエリ復元・フィルタ変換で bool 化する） */
const BOOLEAN_FILTER_KEYS = ['hasDeadline', 'isBroadcast'];

const NoticeManagementPage: React.FC<NoticeManagementPageProps> = ({
  noticeType,
  conditions: baseConditions,
}) => {
  const { users } = useAuth();
  const [searchParams] = useSearchParams();

  // 全通知タブ共通で「未解決のみ」フィルタを先頭に足す（API 非対応・フロント側で絞り込む）
  const conditions = React.useMemo<Condition[]>(
    () => [
      {
        key: UNRESOLVED_FILTER_KEY,
        name: '未解決のみ',
        type: 'check',
        color: '#e53935',
        value: { key: 'true', name: '未解決のみ' },
      },
      ...baseConditions,
    ],
    [baseConditions],
  );
  const [isProgress, setProgress] = useState(true);
  const [notices, setNotices] = useState<AdminNoticeResult[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<AdminNoticeResult | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isEditingNotice, setIsEditingNotice] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  // 未解決フィルタ（API に無いためフロント側で絞り込む）
  const [unresolvedOnly, setUnresolvedOnly] = useState(false);

  const createFormRef = useRef<NoticeCreateFormHandle>(null);
  const updateFormRef = useRef<NoticeUpdateFormHandle>(null);

  // クエリパラメータから初期フィルタを組み立てる（Home 等からの遷移で初期条件を復元する）。
  // conditions に定義されたキーと unresolved のみを対象にする。初回のみ評価。
  const initialFilters = React.useMemo<Filter[]>(() => {
    const result: Filter[] = [];

    // 未解決（unresolved=true）: 特別扱いのチップ
    if (searchParams.get(UNRESOLVED_FILTER_KEY) === 'true') {
      result.push({
        id: generateUUID(),
        key: UNRESOLVED_FILTER_KEY,
        color: '#e53935',
        value: { key: 'true', name: '未解決のみ' },
      });
    }

    // conditions に定義されたキーをクエリから復元する
    conditions.forEach((c) => {
      const raw = searchParams.get(c.key);
      if (raw === null || raw === '') return;
      let name: string;
      if (BOOLEAN_FILTER_KEYS.includes(c.key)) {
        if (raw !== 'true') return; // false は条件なし扱い
        name = c.value?.name ?? c.name;
      } else if (c.type === 'user-select-single') {
        const user = users.find((u) => u.userId === raw);
        name = `${c.name}: ${user ? user.userName : raw}`;
      } else {
        name = `${c.name}: ${raw}`;
      }
      result.push({
        id: generateUUID(),
        key: c.key,
        color: c.color,
        value: { key: raw, name },
      });
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // クエリ由来の初期条件で取得する（未解決はフロント側フィルタなので API へは渡さない）
    setUnresolvedOnly(searchParams.get(UNRESOLVED_FILTER_KEY) === 'true');
    fetchNotices(buildApiParams(initialFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotices = (params: Partial<ListAdminNoticeParams>) => {
    setProgress(true);
    listAdminNotice({ ...params, noticeType })
      .then((result: AdminNoticeResult[]) => {
        setNotices(result);
        setProgress(false);
      })
      .catch((e: Error) => {
        setProgress(false);
        errorDefault('通知の取得に失敗しました');
      });
  };

  const handleUpdate = () => {
    fetchNotices({});
    setSelectedNotice(null);
    setIsCreateMode(false);
  };

  const handleDeleted = () => {
    setNotices((prev) => prev.filter((n) => n.noticeId !== selectedNotice?.noticeId));
    setSelectedNotice(null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedNotice) return;
    setIsDeleteDialogOpen(false);
    setProgress(true);
    deleteAdminNotice({ noticeId: selectedNotice.noticeId })
      .then(() => {
        successDefault('通知を削除しました');
        handleDeleted();
      })
      .catch((e: Error) => {
        errorDefault('通知の削除に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  const handleResolveConfirm = () => {
    if (!selectedNotice) return;
    setIsResolveDialogOpen(false);
    setProgress(true);
    resolveAdminNotice({ noticeId: selectedNotice.noticeId })
      .then(() => {
        successDefault('通知を解決済みにしました');
        handleUpdate();
      })
      .catch((e: Error) => {
        errorDefault('通知の解決に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  // フィルタ配列を API パラメータへ変換する（未解決キーは API に渡さない）
  const buildApiParams = (filters: Filter[]): Partial<ListAdminNoticeParams> => {
    const params: Partial<ListAdminNoticeParams> = {};
    filters.forEach((f) => {
      if (f.key === UNRESOLVED_FILTER_KEY) return; // フロント側フィルタ（API 非対応）
      const key = f.key as keyof ListAdminNoticeParams;
      if (BOOLEAN_FILTER_KEYS.includes(f.key)) {
        (params as any)[key] = f.value.key === 'true';
      } else {
        (params as any)[key] = f.value.key;
      }
    });
    return params;
  };

  const handleSearch = (filters: Filter[]) => {
    setSelectedNotice(null);
    setIsCreateMode(false);
    // 未解決はフロント側フィルタなので state に振り分け、それ以外を API へ渡す
    setUnresolvedOnly(filters.some((f) => f.key === UNRESOLVED_FILTER_KEY));
    fetchNotices(buildApiParams(filters));
  };

  const handleCreateClick = () => {
    setSelectedNotice(null);
    setIsCreateMode(true);
  };

  // 未解決フィルタ適用後の表示用一覧（未解決 = isResolved === false）
  const visibleNotices = React.useMemo(
    () => (unresolvedOnly ? notices.filter((n) => !n.isResolved) : notices),
    [notices, unresolvedOnly],
  );

  const handleSelectItem = (item: ListPanelItem) => {
    setIsCreateMode(false);
    setIsEditingNotice(false);
    const notice = visibleNotices.find((n) => n.noticeId === item.id);
    if (notice) setSelectedNotice(notice);
  };

  const listItems: ListPanelItem[] = React.useMemo(
    () =>
      visibleNotices.map((n) => ({
        id: n.noticeId,
        primary: n.title,
        secondary: formatDate(n.createdAt),
      })),
    [visibleNotices],
  );

  const renderNoticeItem = (item: ListPanelItem) => {
    const notice = visibleNotices.find((n) => n.noticeId === item.id);
    if (!notice) return null;
    return <NoticeListItem notice={notice} />;
  };

  const renderFooter = () => {
    if (isCreateMode) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="作成"
            buttonVariant="primary"
            icon={<NoteAddIcon />}
            onClick={() => createFormRef.current?.submit()}
          />
        </Stack>
      );
    }
    if (selectedNotice && isEditingNotice) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="更新"
            buttonVariant="primary"
            icon={<SaveIcon />}
            onClick={() => updateFormRef.current?.submit()}
          />
        </Stack>
      );
    }
    if (selectedNotice && !isEditingNotice) {
      const canEdit = !selectedNotice.isSystemNotice && !selectedNotice.isResolved;
      const canDelete = !selectedNotice.isSystemNotice;
      const isResolvable =
        !selectedNotice.isResolved &&
        (selectedNotice.noticeType === NOTICE_TYPE_KEY.INFO ||
          selectedNotice.noticeType === NOTICE_TYPE_KEY.IMPORTANT);

      if (canEdit || canDelete || isResolvable) {
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {canDelete && (
              <CustomButton
                label="削除"
                buttonVariant="danger"
                icon={<DeleteIcon />}
                onClick={() => setIsDeleteDialogOpen(true)}
              />
            )}
            {isResolvable && (
              <CustomButton
                label="解決"
                buttonVariant="success"
                icon={<CheckCircleOutlineIcon />}
                onClick={() => setIsResolveDialogOpen(true)}
              />
            )}
            {canEdit && (
              <CustomButton
                label="編集"
                buttonVariant="primary"
                icon={<EditIcon />}
                onClick={() => setIsEditingNotice(true)}
              />
            )}
          </Stack>
        );
      }
    }
    return undefined;
  };

  return (
    <>
      <DomainBreadcrumbs
        items={[
          { label: '管理者メニュー', to: '/management' },
          { label: `通知管理: ${NoticeType[noticeType]}` },
        ]}
      />
      <ConditionList
        conditions={conditions}
        items={listItems}
        selectedId={selectedNotice?.noticeId}
        onSelect={handleSelectItem}
        onSearch={handleSearch}
        onCreateClick={handleCreateClick}
        createButtonLabel={`${NoticeType[noticeType]}を新規作成`}
        showCreateButton={true}
        renderItem={renderNoticeItem}
        isProgress={isProgress}
        collapsible={true}
        detailFooter={renderFooter()}
        conditionKey={`notice-${noticeType}-list`}
        userProfileKey={`management-notice-${noticeType}-list-search`}
        initialFilters={initialFilters}
      >
        {isCreateMode ? (
          <NoticeCreateForm
            ref={createFormRef}
            noticeType={noticeType}
            users={users}
            onCreate={handleUpdate}
            onCancel={() => setIsCreateMode(false)}
            setProgress={setProgress}
          />
        ) : selectedNotice ? (
          <NoticeUpdateForm
            ref={updateFormRef}
            notice={selectedNotice}
            users={users}
            isEditing={isEditingNotice}
            onUpdated={handleUpdate}
            onDeleted={() => setIsDeleteDialogOpen(true)}
            onClose={() => setSelectedNotice(null)}
            onEditingChange={setIsEditingNotice}
            setProgress={setProgress}
          />
        ) : (
          <></>
        )}
      </ConditionList>

      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="通知削除"
      >
        <Alert severity="warning">この操作は取り消せません。削除された通知は復元できません。</Alert>
      </DeleteConfirmDialog>

      <ConfirmDialog
        open={isResolveDialogOpen}
        onClose={() => setIsResolveDialogOpen(false)}
        onConfirm={handleResolveConfirm}
        title="通知を解決済みにする"
        confirmLabel="解決する"
        confirmColor="success"
      >
        <Alert severity="warning">
          この操作は取り消せません。全ユーザーに対して解決済みとなり、通知内容の編集もできなくなります。
        </Alert>
      </ConfirmDialog>
    </>
  );
};

export default NoticeManagementPage;
